import React, { useCallback } from 'react'
import cn from 'clsx'
import { rem } from 'polished'
import styled from 'styled-components'
import Clickable from 'components/Common/Clickable'
import interactiveFocusStyle from 'styles/utils/interactiveFocusStyle'
import { setSvgIconSize } from '../SvgIcon'

/*
  Font size is in px, like Caption, so the label is not affected by the
  20% REM font decrease at mobile sizes.
*/
const Label = styled.span`
  font-family: ${props => props.theme.font.primary.family};
  font-weight: ${props => props.theme.font.primary.weight.regular};
  font-size: 10px;
  line-height: 14px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
`

interface TabElementProps extends React.ComponentProps<typeof Clickable> {
  isActive?: () => boolean
}

const Tab = styled(Clickable)<TabElementProps>`
  display: flex;
  flex: 1 1 0;
  min-width: 0;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${rem(2)};
  padding: ${rem(6)} ${rem(8)};
  border-radius: ${props => props.theme.borderRadius.round};
  color: ${props => props.theme.palette.neutral.default.two};
  text-decoration: none;
  touch-action: manipulation;
  user-select: none;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
  ${setSvgIconSize(20)}

  &:focus {
    ${interactiveFocusStyle}
  }

  &:active,
  &.active {
    background-color: ${props => props.theme.palette.neutral.default.six};
  }

  &.active {
    color: ${props => props.theme.palette.highlight.primary.darkForeground};

    ${Label} {
      font-weight: ${props => props.theme.font.primary.weight.bold};
    }
  }
`

interface Props extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Whether or not the tab is the current one - highlights it and sets `aria-current` */
  isActive?: boolean
  /** Use to link to internal pages */
  to?: string
  /** Use to link to external pages */
  href?: string
  /** Icon shown above the label. Sized to 20px by the tab */
  icon: React.ReactNode
  /** The label text */
  children: React.ReactNode
}

/**
 * One destination in a `AppNavigationBar`: an icon over a short label that fills an equal
 * share of the bar. Renders a link when given `to` or `href`, a button otherwise.
 */
const AppNavigationTab = React.forwardRef<HTMLButtonElement, Props>((props, ref) => {
  const {
    isActive = false,
    icon,
    children,
    className,
    to,
    ...rest
  } = props

  const getIsActive = useCallback(() => isActive, [isActive])

  return <Tab
    {...rest}
    ref={ref}
    to={to}
    // Only NavLink understands this prop, plain anchors and buttons would warn about it
    isActive={to ? getIsActive : undefined}
    aria-current={isActive ? 'page' : undefined}
    className={cn(className, { active: isActive })}
  >
    {icon}
    <Label>{children}</Label>
  </Tab>
})

AppNavigationTab.displayName = 'AppNavigationTab'

export default AppNavigationTab
